<template>
  <Modal :is_show="showModal" class="welcome-message-modal">
    <Loading class="static" v-if="isLoading" />

    <div class="box" v-else>
      <header>
        <h2>Automated Welcome Message</h2>
        <p>
          When automation is turned on, this message will be sent individually to new creators in your Anchor program
          when they create their Anchor profile. Creators will be able to respond.
        </p>
      </header>
      <div class="form-item switch-item">
        <p @click="welcomeMessage.is_enable = !welcomeMessage.is_enable">Automatically send</p>
        <el-switch v-model="welcomeMessage.is_enable" :disabled="permission_mes.editWelcomeMessage.permission == 0"/>
      </div>

      <div class="form-item">
        <label>Message:</label>
        <el-input
          v-model="welcomeMessage.message_text"
          maxlength="5000"
          placeholder="New message"
          show-word-limit
          rows="12"
          type="textarea"
          :readonly="permission_mes.editWelcomeMessage.permission == 0"
        />
      </div>

      <p v-if="isShowError" class="error-msg">Content cannot be empty.</p>

      <footer>
        <el-button @click="showModal = false">Cancel</el-button>
        <el-button type="primary" @click="save" border_r="10" :loading="isSending" :disabled="permission_mes.editWelcomeMessage.permission == 0">Save</el-button>
      </footer>
    </div>
  </Modal>
</template>

<script setup>
import { ref, getCurrentInstance,computed  } from 'vue';
import Modal from '../ui/Modal.vue';
import Loading from '../ui/Loading.vue';
import { ElInput, ElButton, ElSwitch } from 'element-plus';
import log from '../../utils/Log';
import Store from '@/store/index.js';

const app = getCurrentInstance();
const ajax = app.appContext.config.globalProperties.$ajax;
const showModal = ref(false);
const isLoading = ref(true);
const isSending = ref(false);
const welcomeMessage = ref({
  is_enable: false,
  message_text: '',
});
const isShowError = ref(false);
const props = defineProps({
  merchant: {
    type: Object,
    required: true,
  },
});

const permission_mes= computed(()=>{
  return Store.state.user_data?.permission_list?.messagesAndAnnouncements;
});

const getWelcomeMessage = () => {
  ajax.get('/chatroom/get-welcome-message').then((res) => {
    if (res.data.data) {
      welcomeMessage.value.is_enable = res.data.data.is_enable === 1;
      welcomeMessage.value.message_text = res.data.data.message_text;
      log('green', 'Get Welcome Message', welcomeMessage.value);
    } else {
      log('red', 'No Welcome Message', res);
    }

    if (welcomeMessage.value.message_text.trim() === '') {
      welcomeMessage.value.message_text = `Welcome to the ${props.merchant.name} storefront program! To get started, please begin working on your setup checklist – found on the homepage of the Anchor Creator Portal. Once you've added products to your storefront, created collections, and tagged posts, don't forget to add your storefront URL to your link-in-bio tool (i.e. Linktree, hoo.be, etc.). If you have any questions about making the most of your ${props.merchant.name} storefront, please let us know!`;
    }

    isLoading.value = false;
  });
};

const save = () => {
  if (welcomeMessage.value.message_text.trim() === '') {
    isShowError.value = `Content cannot be empty.`;
    return;
  }

  isShowError.value = false;
  isSending.value = true;

  const data = {
    message_text: welcomeMessage.value.message_text,
    is_enable: welcomeMessage.value.is_enable ? 1 : 0,
  };

  ajax.post('/chatroom/save-welcome-message', data).then((res) => {
    log('green', 'Save Welcome Message', res);
    isSending.value = false;
    showModal.value = false;
  });
};

const open = () => {
  getWelcomeMessage();
  showModal.value = true;
};

const close = () => {
  showModal.value = false;
  isShowError.value = false;
};

defineExpose({
  open,
  close,
});
</script>

<style lang="scss">
.welcome-message-modal {
  z-index: 90;

  .box {
    padding: 24px;

    & > header {
      margin-bottom: 16px;

      h2 {
        font: var(--medium-24);
      }

      p {
        margin-top: 16px;
        font: var(--book-14);
        color: var(--Text-default);
      }
    }

    .form-item {
      margin-bottom: 16px;

      &.switch-item {
        display: flex;
        align-items: center;

        p {
          flex: 1;
          cursor: pointer;
          margin: 0;
        }
      }

      label {
        display: block;
        margin-bottom: 8px;
        font: var(--medium-16);

        &::after {
          content: '*';
          color: var(--Red-30);
        }
      }

      p {
        margin-top: 8px;
        font: var(--book-14);
        color: var(--Text-default);
      }
    }

    .error-msg {
      margin-bottom: 4px;
      font: var(--book-12);
      color: var(--Red-30);
    }

    footer {
      display: flex;
      margin-top: 24px;

      .el-button + .el-button {
        flex: 1;
        align-items: center;
        display: flex;
        justify-content: center;
      }
    }
  }
}
</style>
